import './CustomDatePresets.scss';
import cn from 'classnames';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  computeCustomRelativeDate,
  CustomPresetState,
  INIT_CUSTOM_PRESET_STATE
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { DateRange } from '../date-picker-types';
import { SingleCustomDatePreset } from '../SingleCustomDatePreset';

interface CustomDatePresetsProps {
  startCustomPreset: CustomPresetState;
  setStartCustomPreset: Dispatch<SetStateAction<CustomPresetState>>;
  endCustomPreset: CustomPresetState;
  setEndCustomPreset: Dispatch<SetStateAction<CustomPresetState>>;
  rangeError: 'start' | 'end' | null;
  setRangeError: Dispatch<SetStateAction<'start' | 'end' | null>>;
  disabled?: boolean;
  invalidOrOutOfRange: (date: string) => boolean;
  setDateInputs?: Dispatch<SetStateAction<Partial<DateRange>>>;
  fiscalCalOverride?: CompanyFiscalCal;
}

export const CustomDatePresets = ({
  startCustomPreset,
  setStartCustomPreset,
  endCustomPreset,
  setEndCustomPreset,
  disabled,
  rangeError,
  invalidOrOutOfRange,
  setDateInputs,
  setRangeError,
  fiscalCalOverride
}: CustomDatePresetsProps) => {
  const { fiscal_cal } = useCompanyInfo();
  const fiscalCal = useMemo(() => fiscalCalOverride || fiscal_cal, [fiscalCalOverride, fiscal_cal]);
  const onPresetSelect = useCallback(
    (
      state: CustomPresetState,
      setParentDateInput: Dispatch<SetStateAction<Partial<DateRange>>> | undefined,
      isStartDate: boolean,
      rangerErrorSetter: Dispatch<SetStateAction<'start' | 'end' | null>>,
      companionSetter: Dispatch<SetStateAction<CustomPresetState>>
    ) => {
      if (state.number === undefined || state.number < 0) {
        rangerErrorSetter(null);
        return;
      }
      let calculatedDate;
      try {
        calculatedDate = computeCustomRelativeDate(state, fiscalCal);
      } catch {
        rangerErrorSetter(isStartDate ? 'start' : 'end');
      }

      if (calculatedDate && setParentDateInput) {
        if (invalidOrOutOfRange(calculatedDate)) {
          rangerErrorSetter(isStartDate ? 'start' : 'end');
        } else {
          rangerErrorSetter(r => {
            setParentDateInput(p => {
              if (isStartDate) {
                if ((p.end && calculatedDate > p.end) || r === 'end') {
                  companionSetter({ ...INIT_CUSTOM_PRESET_STATE });
                  return { start: calculatedDate };
                }
                return { ...p, start: calculatedDate };
              }
              if ((p.start && calculatedDate < p.start) || r === 'start') {
                companionSetter({ ...INIT_CUSTOM_PRESET_STATE });
                return { end: calculatedDate };
              }
              return { ...p, end: calculatedDate };
            });
            return null;
          });
        }
      }
    },
    [invalidOrOutOfRange, fiscalCal]
  );

  useEffect(() => {
    onPresetSelect(startCustomPreset, setDateInputs, true, setRangeError, setEndCustomPreset);
  }, [startCustomPreset, onPresetSelect, setDateInputs, setEndCustomPreset, setRangeError]);

  useEffect(() => {
    onPresetSelect(endCustomPreset, setDateInputs, false, setRangeError, setStartCustomPreset);
  }, [endCustomPreset, setDateInputs, setRangeError, setStartCustomPreset, onPresetSelect]);

  return (
    <div className={cn('dp-custom-date-presets', { disabled })}>
      <SingleCustomDatePreset
        key='singleDatePresetCustomStart'
        label='Start'
        customPresetState={startCustomPreset}
        setCustomPresetState={setStartCustomPreset}
        disabled={disabled}
        rangeError={rangeError === 'start'}
      />
      <SingleCustomDatePreset
        key='singleDatePresetCustomEnd'
        label='End'
        customPresetState={endCustomPreset}
        setCustomPresetState={setEndCustomPreset}
        disabled={disabled}
        rangeError={rangeError === 'end'}
      />
    </div>
  );
};
